import { nanoid } from 'nanoid';
import { format, parseISO } from 'date-fns';
import { Organisation, ThirdPartyClient } from '../enums/common';
import type { MessageFile } from '../components/Message/index.types';
import { ROUTER_BASENAME } from '../constants/config';

declare global {
    interface Window {
        MSStream: unknown;
        doNotTrack: unknown;
        __THIRD_PARTY_CLIENT__?: ThirdPartyClient;
    }
}

declare global {
    interface Navigator {
        msDoNotTrack: unknown;
        msLaunchUri: (uri: string) => void;
    }
}

export const webConfig = {
    apiKey: 'ixiweb!2$',
    clientId: 'ixiweb',
};

export const mobileConfig = {
    apiKey: 'iximweb!2$',
    clientId: 'iximweb',
};

export const isBrowser = () => typeof window !== 'undefined';

export const insideIFrame = () => {
    if (!isBrowser()) return false;
    return window.location !== window.parent.location;
};

function getUrlParameter(key: string): string | undefined {
    if (!isBrowser()) return undefined;
    const searchParams = decodeURIComponent(window.location.search) || '';

    if (searchParams) {
        let indexOfKey;
        indexOfKey = searchParams.indexOf(`?${key}=`);
        if (indexOfKey !== -1) {
            const str = searchParams.substring(indexOfKey + key.length + 2);
            const indexOfSeperator = str.indexOf('&');
            if (indexOfSeperator === -1) return str;
            return str.substring(0, indexOfSeperator);
        }

        indexOfKey = searchParams.indexOf(`&${key}=`);
        if (indexOfKey !== -1) {
            const str = searchParams.substring(indexOfKey + key.length + 2);
            const indexOfSeperator = str.indexOf('&');
            if (indexOfSeperator === -1) return str;
            return str.substring(0, indexOfSeperator);
        }
    }
    return undefined;
}

export const isMobileDevice = () => {
    if (typeof window === 'undefined') return false;

    const userAgent = window.navigator.userAgent.toLowerCase();
    const mobileKeywords = ['mobile', 'android', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone'];

    return mobileKeywords.some((keyword) => userAgent.includes(keyword));
};

export const isiOSDevice = () =>
    typeof window !== 'undefined' && !window.MSStream && /iPad|iPhone|iPod/.test(navigator.userAgent);

export const getTextFromHTMLString = (htmlString: string) => {
    const div = document.createElement('div');
    div.innerHTML = htmlString;

    return div.textContent || div.innerText;
};

export const copyText = (text: string) => {
    if (typeof navigator === 'undefined') return;
    navigator.clipboard.writeText(text);
};

export const duplicateForSkeleton = <T>(itemToDuplicate: T, times: number) => {
    const duplicate: T[] = [];
    for (let i = 0; i < times; i += 1) {
        const id = nanoid();
        duplicate.push({
            ...itemToDuplicate,
            bookingId: id,
            _id: id,
        });
    }
    return duplicate;
};

function isWebViewUsingUserAgent() {
    const { userAgent } = navigator;
    return userAgent.indexOf('wv') !== -1;
}

// Method 2: Checking window.doNotTrack (Limited to iOS)
function isWebViewUsingDoNotTrack() {
    return (
        window.doNotTrack === undefined && navigator.doNotTrack === undefined && navigator.msDoNotTrack === undefined
    );
}

export const isWebview = () => isWebViewUsingUserAgent() || isWebViewUsingDoNotTrack();

export const isValidMobileNumber = (number: string) => {
    const mobileNumberPattern = /^[0-9]{10}$/;
    return mobileNumberPattern.test(number);
};

export const openEmail = (email: string) => {
    const subject = 'Grievance';
    const body = '';

    const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    if (window.navigator && window.navigator.msLaunchUri) {
        // For Internet Explorer
        return window.navigator.msLaunchUri(mailtoLink);
    }
    // For other browsers
    return window.open(mailtoLink);
};

export const openCall = (mobileNumber: string) => {
    window.open(`tel:${mobileNumber}`);
};

export const getFormattedIxigoMoney = (balance: number, precisionDigit: number = 1) => {
    if (!balance || balance < 0) return '';
    const money =
        balance > 999 ? `${Number((Math.abs(balance) / 1000).toFixed(precisionDigit))}k` : Math.floor(balance);
    return `₹${money}`;
};

export const getCookie = (name: string): string => {
    if (!isBrowser()) return '';
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookies = decodedCookie.split(';').map((cookie) => cookie.trim());
    const matchingCookie = cookies.find((cookie) => cookie.startsWith(`${name}=`));
    return matchingCookie?.substring(name.length + 1) ?? '';
};

export const isMobile = () => {
    if (!isBrowser()) return '';

    // Hardcoded check for ConfirmTkt iOS app since it is not being correctly detected as mobile in iPad
    if (getCookie('clientId') === 'ct-ios') return true;

    return (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(
            navigator.userAgent,
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
            navigator.userAgent.substr(0, 4),
        )
    );
};

export const getClientId = () => {
    const clientId = getCookie('clientId');
    const config = isMobile() ? mobileConfig : webConfig;
    const urlParamClientId = getUrlParameter('clientId');
    return clientId || urlParamClientId || config.clientId;
};

export const isConfirmTicket = (): boolean => {
    const client = getClientId();
    return client === 'confirmtckt';
};

export function isAbhiBus(): boolean {
    const client = getClientId();
    return client === 'abhibus' || client === 'abusad' || client === 'abusio';
}

export function isNiyo(): boolean {
    const client = getClientId();
    return client === 'niyo';
}

export function isMeesho(): boolean {
    const client = getClientId();
    return client === 'meesho';
}

export function isPhonepe(): boolean {
    const client = getClientId();
    return client === 'phonepe';
}

export const isIxigoTrainsApp = (): boolean => {
    const client = getClientId();
    return client === 'ixitrio' || client === 'iximatr';
};

export const isIxigoFlightsApp = (): boolean => {
    const client = getClientId();
    return client === 'iximaio' || client === 'iximaad';
};

export const isIxigoMobileApp = (): boolean => {
    const client = getClientId();
    const mobileClients = ['iximaio', 'iximaad', 'ixitrio', 'iximatr'];
    if (client) return mobileClients.includes(client);
    return false;
};

export const isThirdPartyClient = () => isConfirmTicket() || isAbhiBus() || isNiyo() || isMeesho() || isPhonepe();

export const hideElement = (element: HTMLElement | null) => {
    if (!element) return;
    element.style.maxHeight = '0px';
};

export const showElement = (element: HTMLElement | null) => {
    if (!element) return;
    element.style.maxHeight = `${element.scrollHeight}px`;
};

export const formatDate = (date: Date | number | string, dateFormat = 'E, dd MMM') => {
    if (!date) return '';
    return format(typeof date === 'string' ? parseISO(date) : date, dateFormat) || '';
};

export const decodeHTMLString = (html: string) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = html;
    return textarea.value;
};

export const formatPrice = (
    price?: number | null,
    formatOptions?: { currency?: string; withSeperator?: boolean; decimalNeeded?: boolean; decimalDigits?: number },
): string => {
    const { currency = 'INR', withSeperator = true, decimalNeeded = false, decimalDigits = 2 } = formatOptions || {};
    if (decimalDigits < 0) return '';
    if (typeof price === 'number') {
        return price.toLocaleString('en-IN', {
            style: 'currency',
            currency,
            useGrouping: withSeperator,
            minimumFractionDigits: decimalNeeded ? decimalDigits : 0,
        });
    }
    return '';
};

export const getOrganisation = () => {
    if (typeof window === 'undefined') return Organisation.IXIGO;

    let org = Organisation.IXIGO;
    const { hostname } = window.location;

    if (hostname.includes('bus')) org = Organisation.ABHIBUS;
    if (hostname.includes('confirmtkt')) org = Organisation.CONFIRMTKT;

    return org;
};

const ELITEDESK_HOST_BY_ORG: Record<Organisation, string> = {
    [Organisation.IXIGO]: 'ixigo.elitedesk.in',
    [Organisation.ABHIBUS]: 'abhibus.elitedesk.in',
    [Organisation.CONFIRMTKT]: 'confirmtkt.elitedesk.in',
};

export const getElitedeskOrigin = () => {
    const protocol = isBrowser() ? window.location.protocol : 'https:';
    return `${protocol}//${ELITEDESK_HOST_BY_ORG[getOrganisation()]}`;
};

export const getFileViewUrl = (file: MessageFile): string | undefined => {
    if (!file.url && !file.ref) return undefined;
    if (file.url && /^https?:\/\//i.test(file.url)) return file.url;
    if (file.url) return `${window.location.origin}${ROUTER_BASENAME}${file.url}`;
    return `${window.location.origin}${ROUTER_BASENAME}/files/${file.ref}`;
};

export const getThirdPartyClient = (): ThirdPartyClient | undefined => {
    if (typeof window === 'undefined') return undefined;

    const client = window.__THIRD_PARTY_CLIENT__;
    if (client && Object.values(ThirdPartyClient).includes(client)) return client;

    return undefined;
};
